/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'

import { useAssetGroups } from './useAssetGroups'

interface Props {
  customerId?: number
  /** Fresh company ids; empty means every asset group of the company. */
  value: number[]
  onChange: (ids: number[]) => void
}

/**
 * Multi-select over a customer's asset groups (sub-companies). Blank is "all of them",
 * which the profile stores as [] (['*'] is the every-customer staff scope, not this).
 */
export const AssetGroupsDropdown: React.FC<Props> = ({ customerId, value, onChange }) => {
  const { groups, isLoading, error } = useAssetGroups(customerId)
  const options: DropdownOption[] = groups.map((g) => ({ label: g.name, value: String(g.id) }))
  const selectedItem = options.filter((o) => value.includes(Number(o.value)))
  const placeholder =
    customerId === undefined ? 'Pick a company first' : isLoading ? 'Loading asset groups...' : 'All asset groups'

  return (
    <div data-testid="asset-groups-dropdown">
      <label>Asset groups</label>
      {customerId === undefined ? (
        <div className="input input-bordered flex w-full items-center text-base-content/50">{placeholder}</div>
      ) : (
        <Dropdown
          options={options}
          selectedItem={selectedItem}
          isMultiSelect
          includeSelectAll
          handleMultiSelectItem={(selected: DropdownOption[]) =>
            onChange(selected.filter((o) => o.value !== '' && o.value !== '*').map((o) => Number(o.value)))
          }
          placeholder={placeholder}
        />
      )}
      <span className="label-text-alt mt-1 block text-base-content/60">
        Blank means every asset group of the company.
      </span>
      {error && <span className="label-text-alt mt-1 block text-error">{error}</span>}
    </div>
  )
}

export default AssetGroupsDropdown
